/**
 * Specific heats and latent heats for the heat calculators.
 *
 * Specific heats at 25 °C are from OpenStax Chemistry 2e, Table 5.1 ("Specific Heats of
 * Common Substances at 25 °C and 1 bar"); the few substances that table lacks (silver,
 * mercury, glycerin, benzene, glass) come from OpenStax College Physics 2e, Table 14.1
 * ("Specific Heats of Various Substances"), converted from J/(kg·°C). Latent heats are
 * OpenStax College Physics 2e, Table 14.2 ("Heats of Fusion and Vaporization"), quoted
 * there at the normal melting and boiling points and 1 atm, in kJ/kg (= J/g).
 *
 * All values are rounded to the figures the sources print. Real materials vary with
 * temperature, purity and alloy, so a table value is a starting point, not a property
 * certificate.
 */

export interface SpecificHeatEntry {
  id: string;
  /** J/(g·°C), equal to J/(g·K). */
  value: number;
  source: "chemistry" | "physics";
}

export const SPECIFIC_HEATS: readonly SpecificHeatEntry[] = [
  { id: "water", value: 4.184, source: "chemistry" },
  { id: "ice", value: 2.093, source: "chemistry" },
  { id: "steam", value: 1.864, source: "chemistry" },
  { id: "ethanol", value: 2.376, source: "chemistry" },
  { id: "aluminum", value: 0.897, source: "chemistry" },
  { id: "iron", value: 0.449, source: "chemistry" },
  { id: "copper", value: 0.385, source: "chemistry" },
  { id: "lead", value: 0.13, source: "chemistry" },
  { id: "gold", value: 0.129, source: "chemistry" },
  { id: "silicon", value: 0.712, source: "chemistry" },
  { id: "silver", value: 0.235, source: "physics" },
  { id: "mercury", value: 0.139, source: "physics" },
  { id: "benzene", value: 1.74, source: "physics" },
  { id: "glycerin", value: 2.41, source: "physics" },
  { id: "glass", value: 0.84, source: "physics" },
  { id: "air", value: 1.007, source: "chemistry" },
  { id: "nitrogen", value: 1.04, source: "chemistry" },
  { id: "oxygen", value: 0.918, source: "chemistry" },
  { id: "carbonDioxide", value: 0.853, source: "chemistry" },
  { id: "helium", value: 5.193, source: "chemistry" },
  { id: "argon", value: 0.522, source: "chemistry" },
];

export interface LatentHeatEntry {
  id: string;
  /** Heat of fusion, J/g. */
  fusion: number;
  /** Heat of vaporization, J/g. */
  vaporization: number;
  /** Normal melting point, °C. */
  meltingPoint: number;
  /** Normal boiling point, °C. */
  boilingPoint: number;
}

export const LATENT_HEATS: readonly LatentHeatEntry[] = [
  {
    id: "water",
    fusion: 334,
    vaporization: 2256,
    meltingPoint: 0,
    boilingPoint: 100,
  },
  {
    id: "ethanol",
    fusion: 104,
    vaporization: 854,
    meltingPoint: -114,
    boilingPoint: 78.3,
  },
  {
    id: "ammonia",
    fusion: 108,
    vaporization: 1370,
    meltingPoint: -75,
    boilingPoint: -33.4,
  },
  {
    id: "mercury",
    fusion: 11.8,
    vaporization: 272,
    meltingPoint: -38.9,
    boilingPoint: 357,
  },
  {
    id: "nitrogen",
    fusion: 25.5,
    vaporization: 201,
    meltingPoint: -210,
    boilingPoint: -195.8,
  },
  {
    id: "oxygen",
    fusion: 13.8,
    vaporization: 213,
    meltingPoint: -218.8,
    boilingPoint: -183,
  },
  {
    id: "helium",
    fusion: 5.23,
    vaporization: 20.9,
    meltingPoint: -269.7,
    boilingPoint: -268.9,
  },
  {
    id: "lead",
    fusion: 24.5,
    vaporization: 871,
    meltingPoint: 327,
    boilingPoint: 1750,
  },
  {
    id: "aluminum",
    fusion: 380,
    vaporization: 11_400,
    meltingPoint: 660,
    boilingPoint: 2450,
  },
  {
    id: "silver",
    fusion: 88.3,
    vaporization: 2336,
    meltingPoint: 961,
    boilingPoint: 2193,
  },
  {
    id: "gold",
    fusion: 64.5,
    vaporization: 1578,
    meltingPoint: 1063,
    boilingPoint: 2660,
  },
  {
    id: "copper",
    fusion: 134,
    vaporization: 5069,
    meltingPoint: 1083,
    boilingPoint: 2595,
  },
];

export function findSpecificHeat(id: string): SpecificHeatEntry | undefined {
  return SPECIFIC_HEATS.find((entry) => entry.id === id);
}

export function findLatentHeat(id: string): LatentHeatEntry | undefined {
  return LATENT_HEATS.find((entry) => entry.id === id);
}
